/* Keeping a cursor-following tooltip inside the chart it belongs to.
 *
 * Both charts hang their tooltip off the cursor with `left`/`top` and live inside a container that
 * sets `overflow: hidden`, so a box near an edge is cut off rather than allowed to spill. Flipping
 * it to the other side of the cursor is what avoids that, and it is done with a transform so
 * nothing has to be measured after layout - the box is sized by its own text, which arrives too
 * late to place it with.
 *
 * Shared because the rule is the same on both and the near-miss was not: the points chart flipped
 * horizontally and carried a comment claiming it handled "the right or bottom edge", with no
 * vertical case behind it.
 */

/**
 * Width to keep clear on the right, in CSS pixels.
 *
 * The box is capped at `min(320px, 60%)` and its own text decides the rest, so this is the widest
 * it can plausibly be rather than what it is. Guessing high costs an early flip on a wide pane;
 * guessing low costs a clipped tooltip, which is the failure this exists for.
 */
const CLEARANCE_RIGHT = 220;

/**
 * Height to keep clear at the bottom, in CSS pixels.
 *
 * Four lines plus padding: the contour tooltip is a title and three rows, which is the tallest
 * either chart produces.
 */
const CLEARANCE_BOTTOM = 96;

/** How far the flipped box is pulled back past the cursor, matching the 14px offset it is placed at. */
const FLIP_GAP = 28;

export type TooltipPlacementArgs = {
  /** Where the tooltip is anchored, in CSS pixels within the chart. Null while nothing is hovered. */
  at: { x: number; y: number } | null;
  viewport: { width: number; height: number };
};

/**
 * The transform that flips a tooltip off whichever edges it would otherwise cross.
 *
 * `undefined` rather than `'none'` when it fits, so the style is absent instead of set to a value.
 */
export const tooltipFlipTransform = ({
  at,
  viewport,
}: TooltipPlacementArgs): string | undefined => {
  if (!at) return undefined;

  const flips = [
    viewport.width - at.x < CLEARANCE_RIGHT
      ? `translateX(-100%) translateX(-${FLIP_GAP}px)`
      : null,
    viewport.height - at.y < CLEARANCE_BOTTOM
      ? `translateY(-100%) translateY(-${FLIP_GAP}px)`
      : null,
  ].filter((flip): flip is string => flip !== null);

  return flips.length > 0 ? flips.join(' ') : undefined;
};
